/**
 * Single-source drop slot — accepts exactly one variable (drag or typed),
 * displays it as a removable chip. Smaller than `BuilderDropZone`; used by
 * Negate, Cleanup, Append (parent), Conditional Assign (value), Copy
 * Structure, AppendStruct, and Loop (source field).
 *
 * Drop sources: the QC builder variable drag (`QC_BUILDER_DRAG_TYPE`) and
 * plain text. Typing into the slot is supported so users can reference
 * pipeline-introduced names that don't yet exist in the project tree.
 */
import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faXmark } from "@fortawesome/free-solid-svg-icons"
import { QC_BUILDER_DRAG_TYPE } from "stores/queryBuilderStore"

interface SingleFieldSlotProps {
  value: string
  onChange: (variable: string) => void
  placeholder?: string
}

const SingleFieldSlot: React.FC<SingleFieldSlotProps> = ({
  value,
  onChange,
  placeholder = "Drag a field here, or type a path",
}) => {
  const [dragOver, setDragOver] = React.useState(false)
  // Typing into the slot. Kept separate from `value` so the user can edit
  // a draft (e.g. "Step ") without it being applied mid-keystroke.
  const [draft, setDraft] = React.useState("")

  const handleDragOver = (e: React.DragEvent) => {
    if (
      e.dataTransfer.types.includes(QC_BUILDER_DRAG_TYPE) ||
      e.dataTransfer.types.includes("text/plain")
    ) {
      e.preventDefault()
      e.dataTransfer.dropEffect = "copy"
    }
  }
  const handleDragEnter = (e: React.DragEvent) => {
    if (
      e.dataTransfer.types.includes(QC_BUILDER_DRAG_TYPE) ||
      e.dataTransfer.types.includes("text/plain")
    ) {
      setDragOver(true)
    }
  }
  const handleDragLeave = (e: React.DragEvent) => {
    if (e.currentTarget === e.target) setDragOver(false)
  }
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const v =
      e.dataTransfer.getData(QC_BUILDER_DRAG_TYPE) ||
      e.dataTransfer.getData("text/plain")
    if (v) {
      onChange(v.trim())
      setDraft("")
    }
  }

  const commitDraft = () => {
    const trimmed = draft.trim()
    if (trimmed) {
      onChange(trimmed)
      setDraft("")
    }
  }

  return (
    <div
      className="qc-single-slot"
      data-state={dragOver ? "active" : value ? "filled" : "empty"}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {value ? (
        <span className="qc-single-slot__chip">
          <span className="qc-mono">{value}</span>
          <button
            type="button"
            className="qc-single-slot__clear"
            aria-label="Clear"
            onClick={() => onChange("")}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </span>
      ) : (
        <input
          type="text"
          className="qc-single-slot__input qc-mono"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitDraft}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              commitDraft()
            }
          }}
          placeholder={placeholder}
          aria-label={placeholder}
        />
      )}
    </div>
  )
}

export default SingleFieldSlot
